// Настройки → Garmin: вход (email, пароль, код MFA), статус, синхронизация, история, журнал.
// Пароль живёт только в поле формы и уходит одним запросом; после входа поле очищается.

import { useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { Badge, Button, Card, EmptyState, ErrorText, Input, Modal, PageSpinner, Spinner, useToast, type BadgeTone } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { digitsOnly } from '@/pages/auth/utils'

import {
  useGarminDisconnect,
  useGarminLogin,
  useGarminMfa,
  useGarminStatus,
  useGarminSync,
  useSyncRuns,
  type GarminStatus,
} from './api'
import { RUN_KIND_LABELS, RUN_STATUS_LABELS, backfillText, needsLogin, noDataParts, runLoaded } from './logic'

const STATUS_TONES: Record<GarminStatus['status'], BadgeTone> = {
  connected: 'accent',
  disconnected: 'neutral',
  expired: 'danger',
  error: 'danger',
  rate_limited: 'warning',
}

function fieldError(error: unknown, field: string): string | null {
  return error instanceof ApiError && error.field === field ? error.message : null
}

function generalError(error: unknown): unknown {
  return error instanceof ApiError && (error.field === 'password' || error.field === 'code') ? null : error
}

function LoginCard({ status }: { status: GarminStatus | undefined }) {
  const toast = useToast()
  const sync = useGarminSync()
  const login = useGarminLogin()
  const mfa = useGarminMfa()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [askCode, setAskCode] = useState(Boolean(status?.mfa_pending))

  const connected = () => {
    setPassword('')
    setCode('')
    setAskCode(false)
    toast.show({ message: 'Garmin подключён. Загружаю данные…', tone: 'accent' })
    sync.mutate()
  }

  const submitLogin = (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) return
    mfa.reset()
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (res) => {
          if (res.status === 'needs_mfa') {
            setPassword('')
            setAskCode(true)
          } else connected()
        },
      },
    )
  }

  const submitCode = (e: FormEvent) => {
    e.preventDefault()
    if (code.length < 4) return
    mfa.mutate(code, {
      onSuccess: (res) => {
        if (res.status === 'connected') connected()
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'mfa_expired') setAskCode(false)
      },
    })
  }

  if (askCode) {
    return (
      <Card title="Код подтверждения" subtitle="Garmin отправил код на почту или в приложение. Введи его, чтобы закончить вход.">
        <form onSubmit={submitCode} className="grid gap-4 sm:max-w-sm" noValidate>
          <Input
            label="Код из Garmin"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(digitsOnly(e.target.value))}
            error={fieldError(mfa.error, 'code')}
            autoFocus
          />
          <ErrorText error={generalError(mfa.error)} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" loading={mfa.isPending} disabled={code.length < 4}>
              Подтвердить
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAskCode(false)}>
              Войти заново
            </Button>
          </div>
        </form>
      </Card>
    )
  }

  return (
    <Card
      title={status?.status === 'expired' ? 'Войти в Garmin заново' : 'Подключить Garmin'}
      subtitle="Пароль нужен один раз для входа и нигде не сохраняется — храним только токены Garmin."
    >
      <form onSubmit={submitLogin} className="grid gap-4 sm:max-w-sm" noValidate>
        <Input
          label="Email Garmin Connect"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Пароль Garmin"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldError(login.error, 'password')}
        />
        <ErrorText error={generalError(login.error)} />
        <div>
          <Button type="submit" variant="primary" loading={login.isPending} disabled={!email.trim() || !password}>
            Подключить
          </Button>
        </div>
      </form>
    </Card>
  )
}

function StatusCard({ status }: { status: GarminStatus }) {
  const sync = useGarminSync()
  const disconnect = useGarminDisconnect()
  const toast = useToast()
  const [confirming, setConfirming] = useState(false)
  const loggedIn = !needsLogin(status)

  return (
    <Card
      title="Подключение"
      actions={
        <Badge tone={STATUS_TONES[status.status]}>
          {status.syncing ? 'синхронизация идёт' : status.status === 'connected' ? 'подключено' : status.message}
        </Badge>
      }
    >
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-muted">Состояние</dt>
        <dd className="text-fg">{status.message}</dd>
        {status.email_hint ? (
          <>
            <dt className="text-muted">Аккаунт</dt>
            <dd className="text-fg">{status.email_hint}</dd>
          </>
        ) : null}
        <dt className="text-muted">Последняя удачная</dt>
        <dd className="tabular-nums text-fg">{status.last_success_at ? formatDateTime(status.last_success_at) : 'ещё не было'}</dd>
        {status.backfill.loaded > 0 || loggedIn ? (
          <>
            <dt className="text-muted">История за год</dt>
            <dd className="text-fg">
              {backfillText(status.backfill)}
              {!status.backfill.done ? (
                <span
                  className="mt-1 block h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-surface-2"
                  aria-hidden
                >
                  <span
                    className="block h-full bg-accent"
                    style={{ width: `${Math.round((status.backfill.loaded / status.backfill.total) * 100)}%` }}
                  />
                </span>
              ) : null}
            </dd>
          </>
        ) : null}
      </dl>
      {loggedIn ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            icon="refresh"
            loading={sync.isPending || status.syncing}
            onClick={() =>
              sync.mutate(undefined, {
                onSuccess: (run) =>
                  toast.show({ message: run.status === 'ok' ? 'Синхронизация завершена' : (run.error ?? 'Синхронизация не удалась') }),
              })
            }
          >
            Синхронизировать сейчас
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(true)}>
            Отключить
          </Button>
        </div>
      ) : null}
      <ErrorText error={sync.error} className="mt-2" />
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Отключить Garmin?"
        description="Токены Garmin будут удалены. Уже загруженные сон, шаги и активности останутся."
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button
              variant="danger"
              loading={disconnect.isPending}
              onClick={() =>
                disconnect.mutate(undefined, {
                  onSuccess: () => {
                    setConfirming(false)
                    toast.show({ message: 'Garmin отключён' })
                  },
                })
              }
            >
              Отключить
            </Button>
          </>
        }
      >
        <ErrorText error={disconnect.error} />
      </Modal>
    </Card>
  )
}

function RunsCard({ enabled }: { enabled: boolean }) {
  const runs = useSyncRuns(enabled)
  return (
    <Card title="Журнал синхронизаций" subtitle="Последние 50 запусков.">
      <ErrorText error={runs.error} />
      {runs.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (runs.data ?? []).length === 0 ? (
        <EmptyState icon="history" title="Синхронизаций ещё не было" />
      ) : (
        <ul className="divide-y divide-border text-sm">
          {(runs.data ?? []).map((run) => {
            const missing = noDataParts(run)
            return (
              <li key={run.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
                <span className="w-36 tabular-nums text-muted">{formatDateTime(run.started_at)}</span>
                <span className="text-muted">{RUN_KIND_LABELS[run.kind] ?? run.kind}</span>
                <Badge tone={run.status === 'ok' ? 'accent' : run.status === 'running' ? 'neutral' : 'danger'}>
                  {RUN_STATUS_LABELS[run.status] ?? run.status}
                </Badge>
                <span className="text-fg">{runLoaded(run)}</span>
                {missing.length ? <span className="text-muted">нет данных: {missing.join(', ')}</span> : null}
                {run.error ? <span className="basis-full text-danger">{run.error}</span> : null}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

export function GarminSettingsPage() {
  const status = useGarminStatus({ refetchInterval: 30_000 })
  if (status.isPending) return <PageSpinner />
  return (
    <div className="grid gap-4">
      <ErrorText error={status.error} />
      {status.data ? <StatusCard status={status.data} /> : null}
      {needsLogin(status.data) ? <LoginCard status={status.data} /> : null}
      <RunsCard enabled={Boolean(status.data)} />
    </div>
  )
}
